/**
 * Vastu Room Compatibility Analysis
 * 
 * Calculates compatibility scores (0-10) for rooms in different directions
 * based on:
 * - Lagna (Ascendant) and zodiac elements
 * - Planetary placements and their strength
 * - Functional benefic/malefic status
 * - Yogakaraka planets
 * - Direction-zodiac sign-element relationships
 */

import { PlanetName } from './astroEngine';
import { VastuDirection, getPlanetDirection, PLANET_DIRECTIONS } from './vastuUtils';

// Re-export VastuDirection for convenience
export type { VastuDirection };

// Room type definitions
export type VastuRoomType =
  | 'Main Entrance'
  | 'Living Room'
  | 'Master Bedroom'
  | 'Secondary Bedroom'
  | 'Children Bedroom'
  | 'Guest Bedroom'
  | 'Kitchen'
  | 'Pooja Room'
  | 'Dining Room'
  | 'Study Room'
  | 'Bathroom/Toilet'
  | 'Balcony'
  | 'Storeroom';

export const ROOM_TYPES: VastuRoomType[] = [
  'Main Entrance',
  'Living Room',
  'Master Bedroom',
  'Secondary Bedroom',
  'Children Bedroom',
  'Guest Bedroom',
  'Kitchen',
  'Pooja Room',
  'Dining Room',
  'Study Room',
  'Bathroom/Toilet',
  'Balcony',
  'Storeroom',
];

/**
 * Direction to Zodiac Sign mapping (based on Vastu Kaal Purusha)
 */
const DIRECTION_ZODIAC: Record<VastuDirection, number> = {
  'East': 1,        // Aries (Fire)
  'Southeast': 8,   // Scorpio (Water - but fire direction via Mars)
  'South': 5,       // Leo (Fire)
  'Southwest': 10,  // Capricorn (Earth)
  'West': 7,        // Libra (Air)
  'Northwest': 11,  // Aquarius (Air)
  'North': 4,       // Cancer (Water)
  'Northeast': 12,  // Pisces (Water)
  'Center': 1,      // Brahma sthana (neutral)
};

/**
 * Zodiac element mapping
 */
const ZODIAC_ELEMENTS: Record<number, 'Fire' | 'Earth' | 'Air' | 'Water'> = {
  1: 'Fire', 2: 'Earth', 3: 'Air', 4: 'Water',
  5: 'Fire', 6: 'Earth', 7: 'Air', 8: 'Water',
  9: 'Fire', 10: 'Earth', 11: 'Air', 12: 'Water',
};

/**
 * Traditional Vastu recommendations for rooms (base scores)
 */
const TRADITIONAL_VASTU_SCORES: Record<VastuRoomType, Partial<Record<VastuDirection, number>>> = {
  'Main Entrance': {
    'North': 9, 'Northeast': 10, 'East': 9,
    'West': 6, 'Northwest': 7,
    'South': 4, 'Southeast': 5, 'Southwest': 3
  },
  'Living Room': {
    'North': 8, 'Northeast': 7, 'East': 8, 'Northwest': 8,
    'South': 6, 'West': 7,
    'Southeast': 5, 'Southwest': 4
  },
  'Master Bedroom': {
    'South': 9, 'Southwest': 10, 'West': 8,
    'Northwest': 6,
    'North': 4, 'Northeast': 2, 'East': 5, 'Southeast': 6
  },
  'Secondary Bedroom': {
    'South': 8, 'Southwest': 9, 'West': 9,
    'Northwest': 7, 'North': 6,
    'East': 6, 'Southeast': 5, 'Northeast': 3
  },
  'Children Bedroom': {
    'West': 8, 'Northwest': 9, 'North': 7,
    'East': 7, 'Southeast': 6,
    'South': 5, 'Southwest': 4, 'Northeast': 3
  },
  'Guest Bedroom': {
    'Northwest': 10, 'West': 8, 'North': 7,
    'East': 6,
    'South': 5, 'Southwest': 4, 'Southeast': 5, 'Northeast': 4
  },
  'Kitchen': {
    'Southeast': 10, 'Northwest': 8,
    'East': 6, 'South': 7,
    'North': 4, 'Northeast': 2, 'West': 5, 'Southwest': 3
  },
  'Pooja Room': {
    'Northeast': 10, 'North': 9, 'East': 9,
    'West': 5,
    'Northwest': 4, 'South': 3, 'Southeast': 3, 'Southwest': 2
  },
  'Dining Room': {
    'West': 8, 'East': 8, 'North': 7,
    'Northwest': 7, 'South': 6,
    'Southeast': 5, 'Northeast': 5, 'Southwest': 4
  },
  'Study Room': {
    'North': 9, 'Northeast': 9, 'East': 8, 'West': 8,
    'Northwest': 7,
    'South': 5, 'Southeast': 5, 'Southwest': 4
  },
  'Bathroom/Toilet': {
    'Northwest': 8, 'Southeast': 7, 'West': 7, 'South': 6,
    'East': 4,
    'Southwest': 3, 'North': 2, 'Northeast': 1
  },
  'Balcony': {
    'North': 9, 'East': 9, 'Northeast': 8,
    'West': 7, 'Northwest': 7,
    'South': 5, 'Southeast': 6, 'Southwest': 4
  },
  'Storeroom': {
    'Southwest': 9, 'South': 8, 'West': 8,
    'Northwest': 7,
    'Southeast': 6, 'North': 5, 'East': 5, 'Northeast': 3
  },
};

/**
 * Room-House associations (which houses relate to which room types)
 */
const ROOM_HOUSE_ASSOCIATIONS: Record<VastuRoomType, number[]> = {
  'Main Entrance': [1, 9],              // Self, Fortune
  'Living Room': [1, 11],               // Self, Gains/Friends
  'Master Bedroom': [4, 7],             // Home/Comfort, Spouse
  'Secondary Bedroom': [4, 11],         // Home/Comfort, Guests
  'Children Bedroom': [5],              // Children
  'Guest Bedroom': [11, 12],            // Friends, Hospitality
  'Kitchen': [2, 4],                    // Food/Nourishment, Home
  'Pooja Room': [9, 12],                // Dharma, Spirituality
  'Dining Room': [2, 4],                // Family, Home
  'Study Room': [3, 4, 5],              // Learning, Intelligence, Creativity
  'Bathroom/Toilet': [6, 8, 12],        // Health, Elimination, Loss
  'Balcony': [1, 11],                   // Openness, Gains
  'Storeroom': [2, 4],                  // Wealth storage, Property
};

interface CompatibilityInput {
  placements: Record<string, number>;
  houseLords: Record<number, PlanetName>;
  ascSign: number;
  functionalBenefics?: Set<PlanetName>;
  functionalMalefics?: Set<PlanetName>;
  yogakarakas?: Set<PlanetName>;
  exaltedPlanets?: Record<string, boolean>;
  shadbalaScores?: Partial<Record<PlanetName, number>>;
  blendedScores?: Partial<Record<PlanetName, number>>;
}

/**
 * Calculate compatibility score (0-10) for a room in a specific direction
 */
export function calculateRoomCompatibility(
  direction: VastuDirection,
  roomType: VastuRoomType,
  input: CompatibilityInput
): {
  score: number;
  interpretation: string;
  energyAnalysis: string;
  recommendation: 'preferred' | 'suitable' | 'neutral' | 'avoid' | 'strictly_avoid';
} {
  const {
    placements,
    houseLords,
    ascSign,
    functionalBenefics,
    functionalMalefics,
    yogakarakas,
    exaltedPlanets,
    shadbalaScores,
    blendedScores,
  } = input;

  // Traditional Vastu base score is the dominant factor (65% weight via ±2.5 cap)
  const baseScore = TRADITIONAL_VASTU_SCORES[roomType]?.[direction] ?? 5;
  let chartBonus = 0; // personalised adjustments — capped at ±2.5 before adding to base
  const factors: string[] = [];

  // 1. Direction-Zodiac Element Analysis
  const directionZodiac = DIRECTION_ZODIAC[direction];
  const directionElement = ZODIAC_ELEMENTS[directionZodiac];
  const ascElement = ZODIAC_ELEMENTS[ascSign];

  if (directionElement === ascElement) {
    chartBonus += 0.5;
    factors.push(`Direction element (${directionElement}) matches your ascendant element`);
  } else if (
    (directionElement === 'Fire' && ascElement === 'Air') ||
    (directionElement === 'Air' && ascElement === 'Fire') ||
    (directionElement === 'Earth' && ascElement === 'Water') ||
    (directionElement === 'Water' && ascElement === 'Earth')
  ) {
    chartBonus += 0.3;
    factors.push(`Direction element (${directionElement}) is compatible with ascendant (${ascElement})`);
  }

  // 2. Planetary Influence on Direction
  const planetsInDirection: PlanetName[] = [];
  for (const [planetName, planetDirs] of Object.entries(PLANET_DIRECTIONS)) {
    if (planetDirs.includes(direction)) {
      planetsInDirection.push(planetName as PlanetName);
    }
  }

  for (const planet of planetsInDirection) {
    const shadbala = shadbalaScores?.[planet] || 0;
    const blended = blendedScores?.[planet] || 0;
    const avgStrength = (shadbala + blended) / 2;

    if (avgStrength > 60) {
      chartBonus += 0.5;
      factors.push(`${planet} (ruling ${direction}) is strong in your chart (${avgStrength.toFixed(0)}%)`);
    } else if (avgStrength < 30) {
      chartBonus -= 0.3;
      factors.push(`${planet} (ruling ${direction}) is weak in your chart (${avgStrength.toFixed(0)}%)`);
    }

    if (functionalBenefics?.has(planet)) {
      chartBonus += 0.7;
      factors.push(`${planet} is your functional benefic — enhances ${direction}`);
    } else if (functionalMalefics?.has(planet)) {
      chartBonus -= 0.5;
      factors.push(`${planet} is functional malefic for your ascendant`);
    }

    if (yogakarakas?.has(planet)) {
      chartBonus += 1.0;
      factors.push(`${planet} is Yogakaraka — highly auspicious for ${direction}`);
    }

    if (exaltedPlanets?.[planet]) {
      chartBonus += 0.5;
      factors.push(`${planet} is exalted`);
    }
  }

  // 3. House Lord Analysis for Room Type
  const relevantHouses = ROOM_HOUSE_ASSOCIATIONS[roomType] || [];
  for (const houseNum of relevantHouses) {
    const lord = houseLords[houseNum];
    if (!lord) continue;

    const lordDirs = PLANET_DIRECTIONS[lord] || [];
    if (lordDirs.includes(direction)) {
      chartBonus += 1.0;
      factors.push(`${lord} (lord of H${houseNum} — ${getRoomContext(houseNum)}) naturally rules ${direction}`);

      const lordStrength = ((shadbalaScores?.[lord] || 0) + (blendedScores?.[lord] || 0)) / 2;
      if (lordStrength > 60) {
        chartBonus += 0.5;
        factors.push(`Strong ${lord} (${lordStrength.toFixed(0)}%) amplifies benefit`);
      }
    }
  }

  // 4. Special Room-Specific Adjustments
  if (roomType === 'Pooja Room') {
    const jupiterDirs = PLANET_DIRECTIONS['Jupiter'] || [];
    const ketuDirs = PLANET_DIRECTIONS['Ketu'] || [];
    if (jupiterDirs.includes(direction) || ketuDirs.includes(direction)) {
      chartBonus += 0.5;
      factors.push('Direction has strong spiritual energy (Jupiter/Ketu)');
    }
  }

  if (roomType === 'Kitchen') {
    const marsDirs = PLANET_DIRECTIONS['Mars'] || [];
    if (marsDirs.includes(direction)) {
      const marsStrength = ((shadbalaScores?.['Mars'] || 0) + (blendedScores?.['Mars'] || 0)) / 2;
      if (marsStrength > 50) {
        chartBonus += 0.5;
        factors.push('Mars energy suitable for kitchen fire element');
      }
    }
  }

  if (roomType === 'Master Bedroom') {
    const venusDirs = PLANET_DIRECTIONS['Venus'] || [];
    if (!venusDirs.includes(direction)) {
      chartBonus -= 0.2;
    }
  }

  // Blend: traditional base (dominant) + capped chart personalisation (±2.5 max)
  const clampedBonus = Math.max(-2.5, Math.min(2.5, chartBonus));
  const score = Math.max(0, Math.min(10, baseScore + clampedBonus));

  // Generate interpretation
  let interpretation = '';
  let recommendation: 'preferred' | 'suitable' | 'neutral' | 'avoid' | 'strictly_avoid';

  if (score >= 8) {
    interpretation = 'Highly Compatible — Excellent energy alignment';
    recommendation = 'preferred';
  } else if (score >= 6.5) {
    interpretation = 'Compatible — Good energy flow';
    recommendation = 'suitable';
  } else if (score >= 5) {
    interpretation = 'Neutral — Acceptable with remedies';
    recommendation = 'neutral';
  } else if (score >= 3) {
    interpretation = 'Unfavorable — Not recommended';
    recommendation = 'avoid';
  } else {
    interpretation = 'Highly Unfavorable — Strictly avoid';
    recommendation = 'strictly_avoid';
  }

  const energyAnalysis = factors.length > 0
    ? factors.slice(0, 3).join('. ') + '.'
    : `Traditional Vastu base score: ${baseScore.toFixed(1)}/10 for ${roomType} in ${direction}.`;

  return { score, interpretation, energyAnalysis, recommendation };
}

/**
 * Get best and worst directions for each room type
 */
export function getRoomRecommendations(
  compatibilityMatrix: Record<VastuDirection, Record<VastuRoomType, {
    score: number;
    recommendation: string;
  }>>
): Record<VastuRoomType, {
  preferred: VastuDirection[];
  suitable: VastuDirection[];
  strictlyAvoid: VastuDirection[];
  scores: Record<VastuDirection, number>;
}> {
  const result: any = {};

  for (const roomType of ROOM_TYPES) {
    const directionScores: Array<{ direction: VastuDirection; score: number; rec: string }> = [];

    for (const direction of [...['North', 'Northeast', 'East', 'Southeast', 'South', 'Southwest', 'West', 'Northwest']] as VastuDirection[]) {
      const compat = compatibilityMatrix[direction]?.[roomType];
      if (compat) {
        directionScores.push({
          direction,
          score: compat.score,
          rec: compat.recommendation,
        });
      }
    }

    // Sort by score descending
    directionScores.sort((a, b) => b.score - a.score);

    const preferred = directionScores.filter(d => d.score >= 8).map(d => d.direction);
    const suitable = directionScores.filter(d => d.score >= 6.5 && d.score < 8).map(d => d.direction);
    const strictlyAvoid = directionScores.filter(d => d.score < 3).map(d => d.direction);

    const scores: Record<VastuDirection, number> = {} as any;
    for (const ds of directionScores) {
      scores[ds.direction] = ds.score;
    }

    result[roomType] = {
      preferred,
      suitable,
      strictlyAvoid,
      scores,
    };
  }

  return result;
}

/**
 * Helper: Get context for house number
 */
function getRoomContext(houseNum: number): string {
  const contexts: Record<number, string> = {
    1: 'Self', 2: 'Wealth', 3: 'Siblings', 4: 'Home',
    5: 'Children', 6: 'Health', 7: 'Spouse', 8: 'Longevity',
    9: 'Fortune', 10: 'Career', 11: 'Gains', 12: 'Loss/Spirituality',
  };
  return contexts[houseNum] || `${houseNum}th`;
}
